import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ScrollRestoration, useNavigate, useLocation } from 'react-router';

import AutoScrollToTop from './components/AppLayout/AutoScrollToTop';
import Layout from './components/AppLayout/Layout';
import Authenticator from './components/SecureRoutes/Authenticator';
import { withTdiConnectVariants } from './config';
import CookieBanner from './pages/Cookies/CookieBanner';
import SomethingWentWrong from './pages/Error/SomethingWentWrong';
import { useUserData } from './redux/selectors';
import { resetError } from './redux/status/actions';
import './tokens-override.css';
import './App.css';

interface RootState {
  status: {
    error: string | null;
  };
}

const ONBOARDING_ROUTES: Record<string, string> = {
  onboarding_required: '/onboarding/welcome',
  pending_assignment: '/onboarding/pending',
  onboarding_rejected: '/onboarding/rejected',
};

const ONBOARDING_ADMIN_REVIEW_PATH = '/onboarding/admin-review';
const ONBOARDING_USERS_PATH = '/onboarding/users';
const ONBOARDING_ADMIN_PATHS = [ONBOARDING_ADMIN_REVIEW_PATH];
const ONBOARDING_PUBLIC_TEST_PATHS = [ONBOARDING_USERS_PATH];

// Paths accessible to users who are authenticated but not yet fully onboarded.
const ONBOARDING_ALLOWED_PREFIXES = [
  '/onboarding/',
  '/account',
  '/support-and-feedback',
  '/docs/',
  '/about-us',
  '/contact-us',
  '/contact-sec',
  '/data-privacy',
  '/gtc',
  '/products/compute/specification',
  '/products/caas/specification',
  '/products/baremetal/specification',
];

// Matches `prefix` itself or any of its sub-paths, without matching unrelated
// paths that merely share the same string prefix (e.g. '/account' must not
// match '/accounting').
const isPathWithinPrefix = (pathname: string, prefix: string): boolean => {
  const boundary = prefix.endsWith('/') ? prefix : `${prefix}/`;
  return pathname === prefix || pathname.startsWith(boundary);
};

const AppLayout = (): React.JSX.Element => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const reduxError = useSelector((state: RootState) => state.status.error);
  const user = useUserData();
  const adminRoles = withTdiConnectVariants(['ADMIN']);
  const normalizedRoles = (user.roles ?? []).map(role => role.trim().toUpperCase());
  const isAdmin = normalizedRoles.some(role => adminRoles.includes(role));

  useEffect(() => {
    if (reduxError) {
      dispatch(resetError());
    }
  }, [location, dispatch, reduxError]);

  useEffect(() => {
    if (!user._id) return;

    if (!user.onboardingStatus || user.onboardingStatus === 'onboarded') {
      // Fully onboarded (no status recorded yet, or explicitly marked
      // 'onboarded') — redirect away from onboarding screens if still there.
      if (
        location.pathname.startsWith('/onboarding/') &&
        !(isAdmin && ONBOARDING_ADMIN_PATHS.includes(location.pathname)) &&
        !ONBOARDING_PUBLIC_TEST_PATHS.includes(location.pathname)
      ) {
        void navigate('/', { replace: true });
      }
      return;
    }

    if (ONBOARDING_PUBLIC_TEST_PATHS.includes(location.pathname)) {
      return;
    }

    if (isAdmin && ONBOARDING_ADMIN_PATHS.includes(location.pathname)) {
      return;
    }

    const target = ONBOARDING_ROUTES[user.onboardingStatus as string];
    if (!target || location.pathname === target) return;
    // Only let through non-onboarding allowed pages (docs, specs, etc.).
    // Transitions between onboarding routes must always proceed.
    const isAllowedBypass =
      !location.pathname.startsWith('/onboarding/') &&
      ONBOARDING_ALLOWED_PREFIXES.some(p => isPathWithinPrefix(location.pathname, p));
    if (isAllowedBypass) return;
    void navigate(target, { replace: true });
  }, [user._id, user.onboardingStatus, user.roles, location.pathname, navigate, isAdmin]);

  if (reduxError === 'NETWORK_ERROR') {
    return <SomethingWentWrong />;
  }

  return (
    <>
      {/* We need ScrollToTop in addition to ScrollRestoration because the latter
      misbehaves in some cases. */}
      <AutoScrollToTop />
      <ScrollRestoration />
      <CookieBanner />
      <Authenticator>
        <Layout />
      </Authenticator>
    </>
  );
};

export default AppLayout;
